import { useState, useEffect } from "react";
import { useAuthStore } from "@/core/stores/auth-store";
import { useOperatorStore } from "@/core/stores/operator-store";
import { LoadingSpinner } from "@/core/components/LoadingSpinner";
import { ErrorDisplay } from "@/core/components/ErrorDisplay";
import { Button } from "@/core/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/core/components/ui/command";
import { OperatorApp } from "@/features/operator-selection/types/operator-types";
import { X, ChevronDown } from "lucide-react";
import { Command as CommandPrimitive } from "cmdk";

export default function SelectOperatorPage() {
  const { isAuthenticated, isLoading: authLoading, isDistributor, distributorId } = useAuthStore();
  const { operators, isLoading, error, selectOperator, loadOperators } = useOperatorStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  // Show dropdown when user is typing OR when dropdown is toggled open
  const showDropdown = searchQuery.length > 0 || isOpen;

  // Handler for operator selection
  const handleOperatorSelect = (operator: OperatorApp) => {
    console.log('[SelectOperatorPage] Selecting operator:', operator.name, 'distributorId:', distributorId);
    selectOperator(operator, distributorId);
    setIsOpen(false);
  };

  // Load operators once on mount, passing distributor ID if available
  useEffect(() => {
    if (isAuthenticated) {
      loadOperators(distributorId || undefined);
    }
  }, []);

  // Auto-select for non-distributors with exactly 1 operator
  useEffect(() => {
    console.log('[SelectOperatorPage] Auto-select check:', {
      isLoading,
      isDistributor,
      operatorsLength: operators.length,
      operators: operators.map(o => ({ id: o.operatorId, name: o.name }))
    });
    if (!isLoading && !isDistributor && operators.length === 1) {
      console.log('[SelectOperatorPage] Auto-selecting operator:', operators[0]);
      handleOperatorSelect(operators[0]);
    }
  }, [operators, isDistributor, isLoading]);

  // Clear search query
  const handleClearSearch = () => {
    setSearchQuery("");
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" text="Authenticating..." showText />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="lg" text="Authenticating..." showText />
          <p className="text-sm text-gray-500 mt-4">Please wait while we authenticate you...</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="lg" text="Loading operators..." showText />
          <p className="text-sm text-gray-500 mt-4">Fetching available operators...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md w-full">
          <ErrorDisplay 
            title="Failed to Load Operators"
            message={error || "Unable to fetch operators from the server"}
          />
          <div className="mt-4 text-center">
            <Button onClick={() => loadOperators()} variant="outline">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!operators || operators.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">No Operators Available</h1>
          <p className="text-gray-600 mb-6">
            No operators are available for your account. Please contact support if this seems incorrect.
          </p>
          <Button onClick={() => loadOperators()} variant="outline">
            Refresh
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg border border-gray-200 p-6">
        {/* Operator Label */}
        <label className="block text-sm text-gray-500 mb-2">Operator</label>
        
        {/* Autocomplete Command Component */}
        <Command 
          className="border border-gray-200 rounded-md overflow-visible"
          shouldFilter={true}
        >
          {/* Custom Input with icons - clickable to open dropdown */}
          <div 
            className="relative flex items-center cursor-pointer"
            onClick={() => setIsOpen(true)}
          >
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#FFCD00] rounded-l-md" />
            <CommandPrimitive.Input
              placeholder="Search operators..."
              value={searchQuery}
              onValueChange={(value) => {
                setSearchQuery(value);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
              className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 pl-4 pr-16 cursor-pointer"
            />
            <div className="absolute right-2 flex items-center gap-1">
              {searchQuery && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearSearch();
                  }}
                  className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              )}
              <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </div>
          </div>

          {/* Dropdown List - shows when typing or when dropdown is toggled open */}
          {showDropdown && (
            <CommandList className="border-t border-gray-100 max-h-[300px]">
              <CommandEmpty className="py-6 text-center text-sm text-gray-500">
                No operators found
              </CommandEmpty>
              <CommandGroup>
                {operators.map((operator) => (
                  <CommandItem
                    key={operator.operatorId}
                    value={`${operator.name} ${operator.slug || ''}`}
                    onSelect={() => handleOperatorSelect(operator)}
                    className="px-4 py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0 data-[selected=true]:bg-gray-100"
                  >
                    <span className="text-gray-900">{operator.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          )}
        </Command>

        {/* Non-distributor message */}
        {!isDistributor && (
          <p className="text-sm text-gray-500 mt-4 text-center">
            You are assigned to a single operator
          </p>
        )}
      </div>
    </div>
  );
}
